import React, {useEffect, useMemo, useState} from 'react';
import useSse from "./hooks/use-sse.js";
import useFetchData from "./hooks/use-fetch-data.js";
import projectUtils from "../../../utils/project-utils.js";
import userMessagesService from "../../../services/web/user-messages-service.js";
import {Template} from "../../../components/template/template.jsx";
import useHasPermission from "../../../hooks/use-has-permission.js";
import {PermissionEnum} from "../../../utils/enums/permission-enum.js";
import {
    FaChevronDown,
    FaEnvelope,
    FaFilter,
    FaPaperPlane,
    FaPlus,
    FaRegStar, FaStar,
    FaTrash
} from "react-icons/fa";
import Button from "../../../components/button/button.jsx";
import {Card} from "../../../components/card/card.jsx";
import InputCheckbox from "../../../components/input/input-checkbox.jsx";
import {Menu, MenuButton, MenuItem, MenuItems, Transition} from "@headlessui/react";
import UserMessagesList from "./components/user-messages-list.jsx";
import {FaXmark} from "react-icons/fa6";
import {BsEnvelopeCheckFill} from "react-icons/bs";
import UserMessagesDetails from "./components/user-messages-details.jsx";
import UserMessagesModal from "./components/user-messages-modal.jsx";
import UserMessagesFilterModal from "./components/user-messages-filter-modal.jsx";
import userMessageFilter from "./utils/user-message-filter.jsx";
import {useQueryClient} from "@tanstack/react-query";
import {MESSAGE_COUNT_QUERY_KEY} from "../../../hooks/use-keep-session.js";

const API_URL = import.meta.env.VITE_API_URL;

const UserMessages = () => {
    const queryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selected, setSelected] = useState(undefined);
    const [showFilters, setShowFilters] = useState(false);

    const [filters, setFilters] = useState(undefined);

    const [messagesChecked, setMessagesChecked] = useState([]);

    const {messages} = useSse();
    const [sortBy, setSortBy] = useState("id");
    const [viewSent, setViewSent] = useState(false);

    const {messagesByUser} = useFetchData();

    const hasPermission = useHasPermission();

    useEffect(() => {
        if (selected && !viewSent && !messages.some(item => item.id === selected)) {
            setSelected(undefined);
        }

        setMessagesChecked(prev => prev.filter(id => messages.some(m => m.id === id)));
        queryClient.setQueryData(MESSAGE_COUNT_QUERY_KEY, messages.filter(item => item.status === "NEW").length);
    }, [messages, queryClient]);

    const updateStatus = (status, ids, showMessage = true) => {
        if (status === "EXCLUDED" && showMessage) {
            projectUtils.swalMessage(
                'warning',
                'Excluir',
                `Deseja excluir ${messagesChecked.length > 1 ? 'as mensagens' : 'a mensagem'}?`,
                'Sim',
                true,
                'Não',
                ({isConfirmed}) => {
                    if (!isConfirmed) return;
                    updateStatus(status, ids, false)
                }
            );

            return;
        }

        const formData = new FormData();
        formData.append("ids", ids);
        formData.append("status", status);

        if (ids.length > 1) setIsLoading(true);

        userMessagesService.updateStatus(formData)
            .then(() => {
                if (viewSent) {
                    setMessagesChecked([]);
                    setSelected(undefined);
                    messagesByUser.refetch();
                }

                if (status === "EXCLUDED") {
                    projectUtils.success(false, "Excluído com sucesso");
                }
            })
            .catch(error => {
                console.error(error);
                projectUtils.swalErrorMessage('', 'Não foi possível alterar a mensagem.', 'Ok');
            })
            .finally(() => setIsLoading(false));
    }

    const toggleFavorite = (favorite, ids) => {
        const formData = new FormData();
        formData.append("ids", ids);
        formData.append("favorite", favorite);

        if (ids.length > 1) setIsLoading(true);

        userMessagesService.toggleFavorite(formData)
            .catch(error => {
                console.error(error);
                projectUtils.swalErrorMessage('', 'Não foi possível alterar a mensagem.', 'Ok');
            })
            .finally(() => setIsLoading(false));
    }

    const handleChecked = (isChecked, message) => {
        const newChecked = isChecked
            ? [...messagesChecked, message.id]
            : messagesChecked.filter(id => id !== message.id);

        setMessagesChecked(newChecked);

        setSelected(newChecked.length > 0 ? newChecked[0] : undefined);
    }

    const handleSelectedAll = () => {
        setMessagesChecked(!isAllSelected ? userMessageFilter(messages, filters).map(message => message.id) : []);
        setSelected(undefined);
    };

    const handleDetails = (message) => {
        if (messagesChecked.includes(message.id)) return;

        if (!messagesChecked.includes(message.id) && messagesChecked.length > 0) {
            setMessagesChecked([]);
        }

        setSelected(prev => {
            if (prev === message.id) {
                return undefined;
            }

            if (!viewSent && message.status === "NEW") {
                updateStatus("VIEWED", [message.id]);
            }

            return message.id;
        });
    }

    const handleView = () => {
        setViewSent(!viewSent);
        handleSelectedAll(false);
    }

    const sortItems = [
        {id: "id", text: "Recentes", visible: true},
        {id: "favorite", text: "Favoritos", visible: !viewSent},
        {id: "new", text: "Não visualizadas", visible: !viewSent},
        {id: "important", text: "Importantes", visible: true},
    ]

    const isSelectedValid = viewSent || messages.some(item => item.id === selected);

    const isAllSelected = useMemo(() => {
        const filteredMessages = userMessageFilter(messages, filters);
        return filteredMessages.length === messagesChecked.length;
    }, [messages, filters, messagesChecked]);

    return (
        <>
            <Template isLoading={isLoading}>
                <Template.Header>
                    <Template.Title>
                        Mensagens {viewSent && "- Enviadas"}
                    </Template.Title>

                    {
                        hasPermission(PermissionEnum.CREATE_MESSAGE) &&
                        <div className={"flex gap-2"}>
                            <Button target={viewSent ? "primary" : "secondary"} onClick={handleView}>
                                <FaPaperPlane /> {viewSent ? "Recebidas" : "Enviadas"}
                            </Button>
                            <Button onClick={() => setShowModal(true)}>
                                <FaPlus /> Nova mensagem
                            </Button>
                        </div>
                    }
                </Template.Header>

                <Template.Body>
                    <article className={"grid grid-cols-12 gap-2"}>
                        <Card className={"col-span-4 h-[calc(100vh-69px)]"}>
                            <Card.Body className={"space-y-1 h-full"}>
                                <div className={"flex " + (viewSent ? "justify-end" : "justify-between")}>
                                    {
                                        !viewSent &&
                                        <InputCheckbox
                                            id={"select-all"}
                                            label={"Selecionar todos"}
                                            checked={isAllSelected}
                                            onChange={() => handleSelectedAll()}
                                        />
                                    }

                                    <section className={"flex gap-4 items-center"}>
                                        <Menu as="div">
                                            <MenuButton type="span" className={"flex gap-2 items-center focus:outline-none cursor-pointer h-full"}>
                                                Organizar por <FaChevronDown />
                                            </MenuButton>
                                            <Transition
                                                enter="transition ease-out duration-75"
                                                enterFrom="opacity-0 scale-95"
                                                enterTo="opacity-100 scale-100"
                                                leave="transition ease-in duration-100"
                                                leaveFrom="opacity-100 scale-100"
                                                leaveTo="opacity-0 scale-95"
                                            >
                                                <MenuItems
                                                    anchor="bottom end"
                                                    static={true}
                                                    className="w-36 origin-top-right rounded-md shadow border text-[13px] bg-white [--anchor-gap:var(--spacing-1)] focus:outline-none z-[9999] text-gray-700 flex flex-col mt-1 border-[#bdbdbd]"
                                                >
                                                    {
                                                        sortItems.filter(i => i.visible)?.map(i => (
                                                            <MenuItem key={i.id}>
                                                                <button
                                                                    className={["text-[13px] py-2 px-4 cursor-pointer text-left text-gray-700 block text-sm hover:bg-[#264A6D] hover:text-white", (sortBy === i.id ? "bg-[#264A6D] text-white" : null)].join(" ")}
                                                                    onClick={() => setSortBy(i.id)}
                                                                >
                                                                    {i.text}
                                                                </button>
                                                            </MenuItem>
                                                        ))
                                                    }
                                                </MenuItems>
                                            </Transition>
                                        </Menu>

                                        <button className={"cursor-pointer"} title={"Filtrar por"} onClick={() => setShowFilters(true)}>
                                            <FaFilter />
                                        </button>
                                    </section>
                                </div>

                                <UserMessagesList
                                    viewSent={viewSent}
                                    messages={messages}
                                    messagesByUser={messagesByUser}
                                    sortBy={sortBy}
                                    messagesChecked={messagesChecked}
                                    handleChecked={handleChecked}
                                    selected={selected}
                                    handleDetails={handleDetails}
                                    filters={filters}
                                />
                            </Card.Body>
                        </Card>

                        <Card className={"col-span-8 h-[calc(100vh-69px)]"}>
                            <Card.Body className={"h-full"}>
                                <div className={"h-full flex justify-center items-center"} hidden={messagesChecked?.size > 1 || isAllSelected || !!selected}>
                                    <img
                                        className={"opacity-50 max-w-[90%] max-h-full select-none [-webkit-user-drag:none]"}
                                        src={API_URL + "/corporation/entity/image"}
                                        alt="entity"
                                    />
                                </div>

                                {
                                    messagesChecked.length > 1 &&
                                    <div className="flex justify-center items-center h-full">
                                        <div className="flex flex-col text-[16px] gap-1">
                                            <span className="cursor-pointer flex gap-2 items-center" title="Favoritar mensagem" onClick={() => toggleFavorite(true, userMessageFilter(messages, filters).filter(item => messagesChecked.includes(item.id) && !item.favorite).map(item => item.id))}>
                                                <FaRegStar /> Favoritar mensagem
                                            </span>

                                            <span className="cursor-pointer flex gap-2 items-center" title="Desfavoritar mensagem" onClick={() => toggleFavorite(false, userMessageFilter(messages, filters).filter(item => messagesChecked.includes(item.id) && item.favorite).map(item => item.id))}>
                                                <FaStar /> Remover dos favoritos
                                            </span>

                                            <span className="cursor-pointer flex gap-2 items-center" onClick={() => updateStatus("VIEWED", userMessageFilter(messages, filters).filter(item => messagesChecked.includes(item.id) && item.status !== "VIEWED").map(item => item.id))}>
                                                <BsEnvelopeCheckFill  /> Marcar como lido
                                            </span>

                                            <span className="cursor-pointer flex gap-2 items-center"
                                                  onClick={() => updateStatus("NEW", userMessageFilter(messages, filters).filter(item => messagesChecked.includes(item.id) && item.status !== "NEW").map(item => item.id))}>
                                                <FaEnvelope /> Marcar como não lido
                                            </span>

                                            <span className="cursor-pointer flex gap-2 items-center"
                                                  onClick={() => updateStatus("EXCLUDED", userMessageFilter(messages, filters).filter(item => messagesChecked.includes(item.id)).map(item => item.id))}>
                                                <FaTrash /> Excluir mensagens selecionadas
                                            </span>

                                            <hr className={"opacity-50"}/>

                                            <span className="cursor-pointer flex gap-2 items-center" onClick={() => handleSelectedAll(false)}>
                                                <FaXmark /> Cancelar
                                            </span>
                                        </div>
                                    </div>
                                }

                                {
                                    selected && messagesChecked.length <= 1 && isSelectedValid &&
                                    <UserMessagesDetails
                                        selected={selected}
                                        messages={viewSent ? messagesByUser.data : messages}
                                        returnToDefault={() => setSelected(undefined)}
                                        updateStatus={updateStatus}
                                        viewSent={viewSent}
                                        toggleFavorite={toggleFavorite}
                                    />
                                }
                            </Card.Body>
                        </Card>
                    </article>
                </Template.Body>
            </Template>

            <UserMessagesModal
                open={showModal}
                setOpen={setShowModal}
                refetch={() => messagesByUser.refetch()}
                setIsLoading={setIsLoading}
                isLoading={isLoading}
            />

            <UserMessagesFilterModal
                open={showFilters}
                closeModal={() => setShowFilters(false)}
                filters={filters}
                setFilters={setFilters}
                viewSent={viewSent}
            />
        </>
    );
};

export default UserMessages;